// NeedsReteachingList.js
// Teacher home card surfacing up to 3 worst-off topics from the class's
// mastery data (needs_reteaching[], F3c). Always populated when the class
// has weak topics, even when AttentionList's 5-slot cap has been filled by
// higher-priority items (closing tests, quizzes, inactivity, grading) --
// see home_routes.py's `_needs_reteaching_response`. Not the hero slot;
// AttentionList owns that.
// See docs/superpowers/specs/2026-09-29-home-finish.md §F4b.
import React from "react";

import HomeCard from "../primitives/HomeCard";
import HomeRow from "../primitives/HomeRow";
import HomeSkeleton from "../primitives/HomeSkeleton";
import HomeEmpty from "../primitives/HomeEmpty";

const identity = (s) => s;

// Mirrors home_attention.py's own >=2-point cutoff (see _mastery_items) for
// when a delta is worth mentioning at all, so the two cards read the same
// way for the same underlying number.
const DELTA_THRESHOLD_POINTS = 2;

// `classAveragePercent`/`deltaPoints` are pre-rounded server-side
// (home_routes.py's `_needs_reteaching_response`) with the same `round(x *
// 100)` call the attention "mastery" card uses, so this card can't drift
// from that one on a `.5` boundary by re-rounding the raw floats with JS's
// round-half-up instead of Python's banker's rounding.
const buildSubtitle = (item, t) => {
  const percent = item.classAveragePercent ?? Math.round((item.classAverage ?? 0) * 100);
  // `item.delta` (not `deltaPoints`) is the gate for "is there a delta at
  // all" -- it's what the server sets to null when there's no window data
  // (see mapNeedsReteachingItem), and `deltaPoints` is only ever null in
  // lockstep with it. `deltaPoints` is preferred for the actual number so
  // this card doesn't re-round the raw float, falling back to rounding
  // `delta` itself only for callers that predate the F8 fix.
  if (item.delta != null) {
    const points = item.deltaPoints ?? Math.round(item.delta * 100);
    if (Math.abs(points) >= DELTA_THRESHOLD_POINTS) {
      return points < 0
        ? t("Class average {percent}%, down {points} points this week", { percent, points: Math.abs(points) })
        : t("Class average {percent}%, up {points} points this week", { percent, points: Math.abs(points) });
    }
  }
  return t("Class average {percent}%", { percent });
};

const NeedsReteachingList = ({
  items = [],
  loading = false,
  onOpenTopic,
  isDarkMode = false,
  t = identity,
}) => (
  <HomeCard title={t("Needs reteaching")} isDarkMode={isDarkMode} testID="needs-reteaching-list">
    {loading ? (
      <HomeSkeleton rows={3} isDarkMode={isDarkMode} testID="needs-reteaching-list-skeleton" t={t} />
    ) : items.length === 0 ? (
      <HomeEmpty text={t("Nothing needs reteaching right now.")} isDarkMode={isDarkMode} />
    ) : (
      items.map((item, index) => (
        <HomeRow
          key={item.topic ?? index}
          tag={{ label: t("Reteach"), tone: "warn" }}
          title={item.name}
          subtitle={buildSubtitle(item, t)}
          onPress={() => onOpenTopic?.(item.topic)}
          isDarkMode={isDarkMode}
          first={index === 0}
          testID={`needs-reteaching-row-${index}`}
        />
      ))
    )}
  </HomeCard>
);

export default NeedsReteachingList;
